import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { invoiceService } from '@/services/invoiceService';
// paymentService import removed – receipt download handled in invoiceService
import { formatDate, formatDateTime, getKolkataTodayString } from '@/utils/dateTime';
import { formatPhone, formatPatientAge } from '@/lib/utils';
import { saveBlob } from '@/lib/downloadHelper';
import {
  CreditCard,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Printer,
  Download,
  Receipt,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  IndianRupee,
  RefreshCw,
  Wallet,
  ArrowUpRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PaymentRecordModal } from '@/components/billing/PaymentRecordModal';
import { InvoicePrintModal } from '@/components/billing/InvoicePrintModal';
import { ReceiptPrintModal } from '@/components/billing/ReceiptPrintModal';
import { toast } from 'sonner';

export const PaymentDashboardPage = () => {
  const navigate = useNavigate();
  const { activeClinicId, activeClinic, hospitalId, hospitalName } = useClinic();
  const { user } = useAuth();

  // Date Range Navigation State: 'day' | 'week' | 'month' | 'custom'
  const [dateRangeType, setDateRangeType] = useState('day');
  const [currentDate, setCurrentDate] = useState(() => getKolkataTodayString());
  const [customStartDate, setCustomStartDate] = useState(() => getKolkataTodayString());
  const [customEndDate, setCustomEndDate] = useState(() => getKolkataTodayString());

  // Filter States
  const [activeTab, setActiveTab] = useState('transactions');
  const [paidVia, setPaidVia] = useState('all');
  const [appointmentSource, setAppointmentSource] = useState('all');
  const [settlementStatus, setSettlementStatus] = useState('all');
  const [search, setSearch] = useState('');

  // Data & Loading
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    earnings: 0,
    settled: 0,
    toBeSettled: 0,
    byMode: {},
    invoices: [],
  });

  // Modals
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState(null);
  const [invoicePrintOpen, setInvoicePrintOpen] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
  const [receiptPrintOpen, setReceiptPrintOpen] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeType, currentDate, customStartDate, customEndDate, paidVia, settlementStatus, search, activeClinicId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await invoiceService.getPaymentReport(
        {
          dateRangeType,
          targetDate: currentDate,
          startDate: customStartDate,
          endDate: customEndDate,
          paidVia,
          status: settlementStatus,
          search,
          clinicId: activeClinicId,
        },
        hospitalId
      );

      // Additional filter by source if selected
      let filteredInvoices = res.invoices || [];
      if (appointmentSource !== 'all') {
        filteredInvoices = filteredInvoices.filter(
          (inv) => (inv.source || 'Walk-in').toLowerCase() === appointmentSource.toLowerCase()
        );
      }

      setReportData({
        ...res,
        invoices: filteredInvoices,
      });
    } catch (e) {
      toast.error('Failed to load payments report');
    } finally {
      setLoading(false);
    }
  };

  // Date Navigation Handlers
  const handlePrevDate = () => {
    const d = new Date(currentDate);
    if (dateRangeType === 'day') {
      d.setDate(d.getDate() - 1);
    } else if (dateRangeType === 'week') {
      d.setDate(d.getDate() - 7);
    } else if (dateRangeType === 'month') {
      d.setMonth(d.getMonth() - 1);
    }
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const d = new Date(currentDate);
    if (dateRangeType === 'day') {
      d.setDate(d.getDate() + 1);
    } else if (dateRangeType === 'week') {
      d.setDate(d.getDate() + 7);
    } else if (dateRangeType === 'month') {
      d.setMonth(d.getMonth() + 1);
    }
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const handleResetFilters = () => {
    setPaidVia('all');
    setAppointmentSource('all');
    setSettlementStatus('all');
    setSearch('');
    setCurrentDate(getKolkataTodayString());
  };

  // Date Display Label
  const getDateDisplayLabel = () => {
    const d = new Date(currentDate);
    if (dateRangeType === 'day') {
      return formatDate(currentDate);
    }
    if (dateRangeType === 'week') {
      const curr = new Date(currentDate);
      const first = curr.getDate() - curr.getDay() + (curr.getDay() === 0 ? -6 : 1);
      const mon = new Date(curr.setDate(first));
      const sun = new Date(curr.setDate(first + 6));
      return `${mon.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${sun.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    if (dateRangeType === 'month') {
      return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    return `${formatDate(customStartDate)} to ${formatDate(customEndDate)}`;
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!reportData.invoices || reportData.invoices.length === 0) {
      toast.info('No transactions to export for the selected dates');
      return;
    }

    const headers = [
      'Invoice Number',
      'Date',
      'Patient Name',
      'Patient Code',
      'Doctor',
      'Source',
      'Total Amount (INR)',
      'Paid Amount (INR)',
      'Balance Amount (INR)',
      'Status',
      'Payment Mode',
      'Receipt Numbers',
    ];

    const rows = reportData.invoices.map((inv) => {
      const modes = (inv.payments || []).map((p) => p.paymentMode).join('; ') || 'N/A';
      const receipts = (inv.payments || []).map((p) => p.receiptNumber).join('; ') || 'N/A';
      return [
        inv.invoiceNumber,
        inv.issuedAt ? inv.issuedAt.split('T')[0] : '',
        `"${inv.patientName || ''}"`,
        inv.patientCode || inv.patientId,
        `"${inv.doctorName || ''}"`,
        inv.source || 'Walk-in',
        inv.totalAmount,
        inv.paidAmount,
        inv.balanceAmount,
        inv.status,
        modes,
        receipts,
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `pulseflow_payments_${currentDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Payment transactions exported successfully!');
  };

  // Print Summary
  const handlePrintSummary = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return <Badge className="bg-emerald-600 text-white text-[11px] font-bold">PAID</Badge>;
      case 'PARTIALLY_PAID':
        return <Badge className="bg-amber-500 text-white text-[11px] font-bold">PARTIALLY PAID</Badge>;
      default:
        return <Badge variant="outline" className="border-red-400 text-red-600 bg-red-50 text-[11px] font-bold">PENDING</Badge>;
    }
  };

  return (
    <div className="space-y-6 max-w-7xl pb-16 print:space-y-4 print:pb-0" data-testid="payment-page">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-heading font-bold text-slate-900">Payment</h1>
            <Badge variant="outline" className="bg-teal-50 text-teal-800 border-teal-200 text-xs">
              {activeClinic?.name || 'Main OPD'}
            </Badge>
          </div>
          <p className="text-slate-500 text-xs mt-0.5">
            Real-time collections, payment modes, settlements, and revenue reconciliation
          </p>
        </div>

        {/* Date Range Type Selector */}
        <div className="flex items-center gap-1.5 bg-slate-200/70 p-1 rounded-xl">
          <Button
            size="sm"
            variant={dateRangeType === 'day' ? 'default' : 'ghost'}
            onClick={() => setDateRangeType('day')}
            className={`text-xs h-7 px-3 ${dateRangeType === 'day' ? 'bg-white text-slate-900 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            Day
          </Button>
          <Button
            size="sm"
            variant={dateRangeType === 'week' ? 'default' : 'ghost'}
            onClick={() => setDateRangeType('week')}
            className={`text-xs h-7 px-3 ${dateRangeType === 'week' ? 'bg-white text-slate-900 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            Week
          </Button>
          <Button
            size="sm"
            variant={dateRangeType === 'month' ? 'default' : 'ghost'}
            onClick={() => setDateRangeType('month')}
            className={`text-xs h-7 px-3 ${dateRangeType === 'month' ? 'bg-white text-slate-900 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            Month
          </Button>
          <Button
            size="sm"
            variant={dateRangeType === 'custom' ? 'default' : 'ghost'}
            onClick={() => setDateRangeType('custom')}
            className={`text-xs h-7 px-3 ${dateRangeType === 'custom' ? 'bg-white text-slate-900 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            Custom
          </Button>
        </div>
      </div>

      {/* KPI Top Banner & Date Navigator (Identical to Bajaj Finserv / Online layout) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
        {/* KPI 3-Column Pill Box (8 cols) */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 text-center sm:text-left border-r-0 sm:border-r border-slate-100 pr-4">
            <span className="text-xs text-slate-500 font-medium">Earnings</span>
            <p className="text-2xl font-mono font-bold text-slate-900 mt-0.5">
              ₹{Number(reportData.earnings || 0).toLocaleString('en-IN')}
            </p>
          </div>

          <div className="flex-1 text-center sm:text-left border-r-0 sm:border-r border-slate-100 px-4">
            <span className="text-xs text-slate-500 font-medium">Settled (Collected)</span>
            <p className="text-2xl font-mono font-bold text-emerald-600 mt-0.5">
              ₹{Number(reportData.settled || 0).toLocaleString('en-IN')}
            </p>
          </div>

          <div className="flex-1 text-center sm:text-left pl-4">
            <span className="text-xs text-slate-500 font-medium">To be Settled (Pending)</span>
            <p className="text-2xl font-mono font-bold text-red-600 mt-0.5">
              ₹{Number(reportData.toBeSettled || 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* Date Navigator Pill Box (4 cols) */}
        <div className="lg:col-span-4 bg-sky-50/70 border border-sky-200/80 rounded-2xl p-3 flex items-center justify-between shadow-sm">
          {dateRangeType !== 'custom' ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevDate}
                className="h-8 w-8 text-sky-700 hover:bg-sky-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1.5 text-xs font-bold text-sky-900">
                <CalendarIcon className="h-4 w-4 text-sky-600" />
                <span>{getDateDisplayLabel()}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNextDate}
                className="h-8 w-8 text-sky-700 hover:bg-sky-100"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 w-full">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="text-xs h-8 bg-white"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="text-xs h-8 bg-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-2 print:hidden">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="transactions" className="text-xs font-semibold data-[state=active]:bg-white">
              Transactions ({reportData.invoices.length})
            </TabsTrigger>
            <TabsTrigger value="settlement" className="text-xs font-semibold data-[state=active]:bg-white">
              Settlement by Payment Mode
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintSummary}
              className="text-xs border-slate-300 text-slate-700"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Summary
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="text-xs border-slate-300 text-slate-700"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters Strip */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-3 text-xs print:hidden">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <span className="font-bold text-slate-700 uppercase tracking-wider text-[11px] flex items-center gap-1">
              <Filter className="h-3.5 w-3.5 text-teal-600" /> Filter By:
            </span>

            {/* Paid Via */}
            <div className="w-36">
              <Select value={paidVia} onValueChange={setPaidVia}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Paid Via" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Paid Via: All</SelectItem>
                  <SelectItem value="UPI">UPI (GPay / PhonePe)</SelectItem>
                  <SelectItem value="CASH">Cash Counter</SelectItem>
                  <SelectItem value="CARD">Card (POS)</SelectItem>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div className="w-40">
              <Select value={appointmentSource} onValueChange={setAppointmentSource}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Source: All</SelectItem>
                  <SelectItem value="Walk-in">Walk-in Visit</SelectItem>
                  <SelectItem value="Online Booking">Online Booking</SelectItem>
                  <SelectItem value="Referral">Doctor Referral</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Settlement Status */}
            <div className="w-40">
              <Select value={settlementStatus} onValueChange={setSettlementStatus}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Status: All</SelectItem>
                  <SelectItem value="PAID">Settled (Paid)</SelectItem>
                  <SelectItem value="PARTIALLY_PAID">Partially Paid</SelectItem>
                  <SelectItem value="PENDING">To be Settled (Pending)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-teal-600 text-xs hover:bg-teal-50 h-8 px-2.5 font-semibold"
            >
              Clear
            </Button>
          </div>

          {/* Search box */}
          <div className="relative min-w-[220px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search by Patient, ID, or Invoice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </div>

        {/* TAB 1: TRANSACTIONS TABLE */}
        <TabsContent value="transactions" className="space-y-4 mt-0">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : reportData.invoices.length === 0 ? (
            <div className="p-16 text-center bg-white rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                <CreditCard className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">
                  There are no payments to be shown for selected dates
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Try changing your date filter, switching clinics, or clearing search filters.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs border-teal-300 text-teal-700"
              >
                Reset to Today's Transactions
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 uppercase text-[10px] tracking-wider font-bold">
                      <th className="py-3 px-4">Date of Appt/Invoice</th>
                      <th className="py-3 px-4">Invoice #</th>
                      <th className="py-3 px-4">Patient Name</th>
                      <th className="py-3 px-4">Doctor / Source</th>
                      <th className="py-3 px-4 text-right">Payable Amount</th>
                      <th className="py-3 px-4 text-right">Paid Amount</th>
                      <th className="py-3 px-4 text-right">Pending Amount</th>
                      <th className="py-3 px-4 text-center">Settlement Status</th>
                      <th className="py-3 px-4 text-center print:hidden">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.invoices.map((inv) => {
                      const latestPayment = inv.payments?.[inv.payments.length - 1];

                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="py-3 px-4 text-slate-700 whitespace-nowrap">
                            <span className="font-semibold block text-slate-900">
                              {formatDate(inv.issuedAt || inv.createdAt)}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {inv.issuedAt ? inv.issuedAt.split('T')[1]?.substring(0, 5) : 'OPD'}
                            </span>
                          </td>

                          <td className="py-3 px-4 font-mono font-bold text-slate-900 whitespace-nowrap">
                            {inv.invoiceNumber}
                          </td>

                          <td className="py-3 px-4">
                            <span className="font-bold text-slate-900 block text-sm">
                              {inv.patientName || 'Patient'}
                            </span>
                            <span className="text-[10px] text-slate-500 font-mono">
                              {inv.patientCode || inv.patientId}
                            </span>
                          </td>

                          <td className="py-3 px-4 text-slate-700">
                            <span className="font-medium text-slate-900 block">{inv.doctorName || 'Dr. Consultant'}</span>
                            <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 px-1.5 py-0">
                              {inv.source || 'Walk-in'}
                            </Badge>
                          </td>

                          <td className="py-3 px-4 text-right font-mono font-bold text-slate-900">
                            ₹{Number(inv.totalAmount).toFixed(2)}
                          </td>

                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <span className="font-mono font-bold text-emerald-700 block">
                              ₹{Number(inv.paidAmount).toFixed(2)}
                            </span>
                            {latestPayment && (
                              <span className="text-[10px] text-slate-500 bg-emerald-50 px-1.5 py-0.5 rounded font-mono">
                                {latestPayment.paymentMode}
                              </span>
                            )}
                          </td>

                          <td className="py-3 px-4 text-right font-mono font-bold">
                            {Number(inv.balanceAmount) > 0 ? (
                              <span className="text-red-600">₹{Number(inv.balanceAmount).toFixed(2)}</span>
                            ) : (
                              <span className="text-slate-400">₹0.00</span>
                            )}
                          </td>

                          <td className="py-3 px-4 text-center whitespace-nowrap">
                            {getStatusBadge(inv.status)}
                          </td>

                          <td className="py-3 px-4 text-center whitespace-nowrap print:hidden">
                            <div className="flex items-center justify-center gap-1.5">
                              {Number(inv.balanceAmount) > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedInvoiceForPayment(inv);
                                    setPaymentModalOpen(true);
                                  }}
                                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-2.5 shadow-sm"
                                >
                                  Collect
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInvoiceForPrint(inv);
                                  setInvoicePrintOpen(true);
                                }}
                                className="h-7 text-xs border-slate-300 text-slate-700"
                              >
                                Invoice
                              </Button>
                              {inv.payments?.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedPaymentForReceipt(inv.payments[inv.payments.length - 1]);
                                    setSelectedInvoiceForPrint(inv);
                                    setReceiptPrintOpen(true);
                                  }}
                                  className="h-7 text-xs text-teal-700 hover:bg-teal-50"
                                >
                                  Receipt
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* TAB 2: SETTLEMENT BY PAYMENT MODE */}
        <TabsContent value="settlement" className="space-y-6 mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  UPI Collections
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-2xl font-mono font-bold text-slate-900">
                  ₹{Number(reportData.byMode?.UPI?.amount || 0).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-slate-500">
                  {reportData.byMode?.UPI?.count || 0} transactions (GPay / PhonePe / QR)
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Cash Counter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-2xl font-mono font-bold text-slate-900">
                  ₹{Number(reportData.byMode?.CASH?.amount || 0).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-slate-500">
                  {reportData.byMode?.CASH?.count || 0} cash counter receipts
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Card / POS Terminal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-2xl font-mono font-bold text-slate-900">
                  ₹{Number(reportData.byMode?.CARD?.amount || 0).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-slate-500">
                  {reportData.byMode?.CARD?.count || 0} credit/debit card swipes
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Bank / Cheque / Other
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-2xl font-mono font-bold text-slate-900">
                  ₹{(
                    Number(reportData.byMode?.BANK_TRANSFER?.amount || 0) +
                    Number(reportData.byMode?.CHEQUE?.amount || 0) +
                    Number(reportData.byMode?.OTHER?.amount || 0)
                  ).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-slate-500">
                  Direct transfers and other clearing methods
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Settlement Progress Reconciliation */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-teal-600" />
                Settlement Status Reconciliation
              </CardTitle>
              <CardDescription className="text-xs">
                Comparison between total billed earnings and settled collections for {getDateDisplayLabel()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-medium">
                  <span className="text-slate-600">Collection Recovery Rate:</span>
                  <span className="font-mono font-bold text-emerald-700">
                    {reportData.earnings > 0
                      ? ((reportData.settled / reportData.earnings) * 100).toFixed(1)
                      : 0}
                    %
                  </span>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div
                    className="bg-emerald-600 h-full transition-all"
                    style={{
                      width: `${reportData.earnings > 0 ? (reportData.settled / reportData.earnings) * 100 : 0}%`,
                    }}
                  />
                  <div
                    className="bg-red-500 h-full transition-all"
                    style={{
                      width: `${reportData.earnings > 0 ? (reportData.toBeSettled / reportData.earnings) * 100 : 0}%`,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-600 inline-block" /> Settled: ₹
                    {Number(reportData.settled).toLocaleString('en-IN')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500 inline-block" /> Pending: ₹
                    {Number(reportData.toBeSettled).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Recording Modal */}
      {selectedInvoiceForPayment && (
        <PaymentRecordModal
          open={paymentModalOpen}
          onOpenChange={(val) => {
            setPaymentModalOpen(val);
            if (!val) setSelectedInvoiceForPayment(null);
          }}
          invoice={selectedInvoiceForPayment}
          onPaymentSuccess={() => {
            loadData();
          }}
          onViewReceipt={(payment, inv) => {
            setSelectedPaymentForReceipt(payment);
            setSelectedInvoiceForPrint(inv);
            setReceiptPrintOpen(true);
          }}
        />
      )}

      {/* Invoice Print Modal */}
      {selectedInvoiceForPrint && (
        <InvoicePrintModal
          open={invoicePrintOpen}
          onOpenChange={(val) => {
            setInvoicePrintOpen(val);
            if (!val) setSelectedInvoiceForPrint(null);
          }}
          invoice={selectedInvoiceForPrint}
          patient={{
            firstName: selectedInvoiceForPrint.patientName?.split(' ')[0],
            lastName: selectedInvoiceForPrint.patientName?.split(' ').slice(1).join(' '),
            patientCode: selectedInvoiceForPrint.patientCode,
          }}
          doctor={{ name: selectedInvoiceForPrint.doctorName }}
          clinicName={activeClinic?.name}
          hospitalName={hospitalName}
        />
      )}

      {/* Receipt Print Modal */}
      {selectedPaymentForReceipt && selectedInvoiceForPrint && (
        <ReceiptPrintModal
          open={receiptPrintOpen}
          onOpenChange={(val) => {
            setReceiptPrintOpen(val);
            if (!val) setSelectedPaymentForReceipt(null);
          }}
          payment={selectedPaymentForReceipt}
          invoice={selectedInvoiceForPrint}
          patient={{
            firstName: selectedInvoiceForPrint.patientName?.split(' ')[0],
            lastName: selectedInvoiceForPrint.patientName?.split(' ').slice(1).join(' '),
            patientCode: selectedInvoiceForPrint.patientCode,
          }}
          clinicName={activeClinic?.name}
          hospitalName={hospitalName}
        />
      )}
    </div>
  );
};

export default PaymentDashboardPage;

